import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { IMAGES } from "@/data";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 260]);
  const lines = t("hero.lines");
  const stats = t("hero.stats");

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 scale-110" style={{ y: yBg }}>
        <img src={IMAGES.hero} alt="Navire porte-conteneurs en mer" fetchpriority="high" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-abyss via-abyss/20 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs font-bold tracking-[0.3em] uppercase text-ember mb-8"
          data-testid="hero-overline"
        >
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-display font-bold uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-title">
          {lines.map((line, i) => (
            <MaskedLine key={i} delay={0.15 + i * 0.15}>
              {i === lines.length - 1 ? <span className="text-ember">{line}</span> : line}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-steel-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button onClick={() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-quote-btn"
            className="bg-ember hover:bg-emberdark text-white font-semibold px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-1 flex items-center gap-2">
            {t("hero.cta1")} <ArrowRight size={16} />
          </button>
          <button onClick={() => document.getElementById("destinations")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-destinations-btn"
            className="border border-white/20 hover:border-white/60 text-white font-semibold px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-1">
            {t("hero.cta2")}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
          data-testid="hero-stats"
        >
          {stats.map((s, i) => (
            <div key={i} className="bg-abyss/80 backdrop-blur-sm p-6">
              <p className="font-display text-3xl lg:text-4xl font-semibold text-white">{s.value}</p>
              <p className="text-xs uppercase tracking-widest text-steel-500 mt-2">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 right-6 lg:right-12 z-10 text-steel-500 flex items-center gap-2 text-xs uppercase tracking-widest"
      >
        {t("hero.scroll")} <ArrowDown size={14} />
      </motion.div>
    </section>
  );
};
